import React from 'react';
import {Alert, AlertTitle, Box, Chip, Link, Table, TableBody, TableCell, TableRow, Typography} from '@mui/material';
import {useHistory} from 'react-router-dom';
import {RequestTracker} from '../../../../Component/Model/Dto/Staging/RequestTracker';
import {RequestStatus, ValidationStatus} from '../../../../Component/Model/Enum/ImportEnum';
import {StringDateTimeHelper} from '../../../../Component/Model/helpers';
import {MDBox} from '../../../../Component/Common/Components/MDBox';
import {StagedImportFileLinks} from '../../../Common/ImportAndExport/StagedImportFileLinks';
import {ImportStep, stepFieldWidth} from './ImportStep';

const inProgressStatuses = [RequestStatus.NewRequest, RequestStatus.FetchingPayroll];
const failedStatuses = [RequestStatus.RequestFailed, RequestStatus.FetchFailed];

interface IProps {
    selectedOrganisation: number;
    requestTracker?: RequestTracker.RequestTrackerDetail;
    requestEmployees: () => void;
    openFetchDataScreen: () => void;
}

export const FetchedDataStep: React.FC<IProps> =
    ({
         selectedOrganisation,
         requestTracker,
         requestEmployees,
         openFetchDataScreen
     }) => {

        const history = useHistory();

        const status = requestTracker?.status ?? -1;
        const isFetching = inProgressStatuses.includes(status);
        const hasFailed = failedStatuses.includes(status);
        const hasExpired = status === RequestStatus.Expired;
        const organisationSelected = selectedOrganisation > 0;
        const stagingDetails = requestTracker?.stagingDetails ?? [];
        const salaryMonths = requestTracker?.salaryBatch?.months ?? [];

        const localTime = (date?: Date) => StringDateTimeHelper(date?.toString() ?? '', true);

        const openStagedData = () => {
            if (requestTracker) {
                history.push(`/organisation/payrollData/staged/${requestTracker.requestId}`);
            }
        };

        const openValidationReview = () => {
            if (requestTracker) {
                history.push(`/organisation/payrollData/review/${requestTracker.requestId}`);
            }
        };

        const describeFetch = (): string => {
            if (!organisationSelected) {
                return 'Where the data was fetched from, and what it holds.';
            }
            if (!requestTracker) {
                return 'Nothing has been fetched for this organisation yet.';
            }
            if (isFetching) {
                return `Started ${localTime(requestTracker.createdOn)} · still fetching`;
            }
            if (hasExpired) {
                return `Fetched ${localTime(requestTracker.createdOn)} · expired`;
            }
            if (hasFailed) {
                return `Started ${localTime(requestTracker.createdOn)} · did not complete`;
            }
            return `Completed ${localTime(requestTracker.completedOn) || localTime(requestTracker.createdOn)} · from your connected payroll`;
        };

        const fetchDataLink = (label: string) => (
            <Link
                component="button"
                type="button"
                onClick={requestEmployees}
                underline="always"
                color="primary"
                sx={{cursor: 'pointer', typography: 'caption', alignSelf: 'flex-start'}}>
                {label}
            </Link>
        );

        const renderRecordsPerDataType = () => (
            <Box sx={{maxWidth: stepFieldWidth, overflowX: 'auto'}}>
                <Table size="small">
                    <MDBox component="thead">
                        <TableRow>
                            <TableCell>Data type</TableCell>
                            <TableCell align="right">Records</TableCell>
                        </TableRow>
                    </MDBox>
                    <TableBody>
                        {stagingDetails.map(detail => (
                            <TableRow key={detail.entityName}>
                                <TableCell>{detail.entityName}</TableCell>
                                <TableCell align="right">{detail.totalRecords ?? 0}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Box>
        );

        const renderStepPerDataType = () => (
            <Box>
                <Typography variant="caption" fontWeight="bold" color="secondary" display="block">
                    {hasFailed ? 'Last successful step before the error' : 'Progress per data type'}
                </Typography>
                {stagingDetails.map(detail => (
                    <Typography key={detail.entityName} variant="caption" display="block">
                        • {detail.entityName}: {detail.lastSuccessfulStep ?? 'Pending'}
                        {!isFetching && detail.totalRecords != null
                            ? ` (${detail.totalRecords} record${detail.totalRecords === 1 ? '' : 's'})`
                            : ''}
                    </Typography>
                ))}
            </Box>
        );

        const renderSalaryMonths = () => (
            <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center'}}>
                <Typography variant="caption" fontWeight="bold" color="secondary">
                    Salary months
                </Typography>
                {salaryMonths.map(month => (
                    <Chip key={month.month} label={month.month} size="small" variant="outlined"/>
                ))}
            </Box>
        );

        const renderValidation = () => {
            const validation = requestTracker?.validationStatus;

            if (validation === ValidationStatus.Validating) {
                return <Alert severity="info">Validating the fetched data…</Alert>;
            }

            if (validation === ValidationStatus.Passed) {
                return (
                    <Alert severity="success">
                        <AlertTitle>Validation passed</AlertTitle>
                        The fetched data is well-formed.{' '}
                        <Link
                            component="button"
                            type="button"
                            onClick={openValidationReview}
                            underline="always"
                            color="inherit"
                            sx={{cursor: 'pointer', typography: 'caption'}}>
                            Review validation
                        </Link>
                    </Alert>
                );
            }

            if (validation === ValidationStatus.Failed) {
                return (
                    <Alert severity="error">
                        <AlertTitle>Validation found issues</AlertTitle>
                        These must be resolved before the data can be imported.{' '}
                        <Link
                            component="button"
                            type="button"
                            onClick={openValidationReview}
                            underline="always"
                            color="inherit"
                            sx={{cursor: 'pointer', typography: 'caption'}}>
                            Review validation issues
                        </Link>
                    </Alert>
                );
            }

            if (validation === ValidationStatus.CouldNotComplete) {
                return (
                    <Alert severity="warning">
                        <AlertTitle>Validation could not complete</AlertTitle>
                        The fetched data was not checked. Re-run the validation from the payroll requests screen.
                    </Alert>
                );
            }

            return null;
        };

        const renderFetch = () => {
            if (!organisationSelected) {
                return (
                    <Typography variant="caption" color="secondary">
                        Choose an organisation in step 1.
                    </Typography>
                );
            }

            if (!requestTracker) {
                return (
                    <>
                        <Typography variant="caption" color="secondary">
                            Data has not been fetched from your connected payroll yet.
                        </Typography>
                        {fetchDataLink('Fetch data now')}
                    </>
                );
            }

            if (hasExpired) {
                return (
                    <>
                        <Alert severity="warning">
                            The data fetched on {localTime(requestTracker.createdOn)} has expired and can no longer be
                            imported.
                        </Alert>
                        {fetchDataLink('Fetch data again')}
                    </>
                );
            }

            return (
                <>
                    {hasFailed &&
                        <Alert severity="error">
                            <AlertTitle>{requestTracker.statusDescription}</AlertTitle>
                            {requestTracker.message && requestTracker.message !== requestTracker.statusDescription
                                ? requestTracker.message
                                : 'The fetch did not complete, so there is nothing staged to import.'}
                        </Alert>
                    }

                    {isFetching &&
                        <Alert severity="info">Fetching data from your connected payroll…</Alert>
                    }

                    {stagingDetails.length > 0 && (isFetching || hasFailed) && renderStepPerDataType()}
                    {stagingDetails.length > 0 && !isFetching && !hasFailed && renderRecordsPerDataType()}
                    {stagingDetails.length === 0 && !isFetching && !hasFailed &&
                        <Typography variant="caption" color="secondary">
                            The fetch completed without staging any records.
                        </Typography>
                    }
                    {salaryMonths.length > 0 && !isFetching && renderSalaryMonths()}
                    {stagingDetails.length > 0 && !isFetching && !hasFailed && requestTracker &&
                        <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center'}}>
                            <StagedImportFileLinks
                                requestId={requestTracker.requestId}
                                hasStagedSalaries={requestTracker.hasStagedSalaries}/>
                            <Link
                                component="button"
                                type="button"
                                onClick={openStagedData}
                                underline="always"
                                color="primary"
                                sx={{cursor: 'pointer', typography: 'caption'}}>
                                Browse the staged data
                            </Link>
                        </Box>
                    }

                    {renderValidation()}

                    {hasFailed &&
                        <Link
                            component="button"
                            type="button"
                            onClick={openFetchDataScreen}
                            underline="always"
                            color="primary"
                            sx={{cursor: 'pointer', typography: 'caption', alignSelf: 'flex-start'}}>
                            View details
                        </Link>
                    }
                </>
            );
        };

        return (
            <ImportStep stepNumber={2} title="What was fetched" description={describeFetch()}>
                {renderFetch()}
            </ImportStep>
        );
    };
